import LegalShell from "@/pages/LegalShell";

export default function Terms() {
  return (
    <LegalShell kind="terms" title="Terms & Conditions" updated="Last updated: 30 April 2026 · Effective: 30 April 2026">
      <p>
        These Terms and Conditions ("Terms") govern your use of the Zoku mobile application and
        website located at zoku.in (collectively, the "Service"), operated by Zoku ("we", "us" or
        "our"). By downloading, installing or using the Service you agree to be bound by these
        Terms. If you do not agree, please do not use the Service.
      </p>

      <h2>1. Eligibility</h2>
      <p>
        You must be at least 13 years of age to use the Service. By using the Service you
        represent and warrant that you meet this requirement. If you are under 18, you confirm
        that a parent or guardian has consented to these Terms on your behalf.
      </p>

      <h2>2. Description of Service</h2>
      <p>
        Zoku provides a platform that allows users to scan barcodes and food labels, analyse
        ingredient safety, track calorie and nutritional intake, and access AI-generated summaries
        about food and skincare products. The information provided by the Service is for general
        informational and educational purposes only and does not constitute medical, nutritional
        or professional advice.
      </p>

      <h2>3. Account Registration</h2>
      <p>
        Certain features of the Service require you to create an account. You agree to provide
        accurate, current and complete information during registration and to update such
        information to keep it accurate. You are responsible for maintaining the confidentiality
        of your account credentials and for all activities that occur under your account. You must
        notify us immediately at <a href="mailto:support@zoku.in">support@zoku.in</a> if you
        suspect any unauthorised use of your account.
      </p>

      <h2>4. Acceptable Use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Service for any unlawful purpose or in violation of any applicable laws or regulations.</li>
        <li>Reproduce, duplicate, copy, sell, or exploit any portion of the Service without our express written permission.</li>
        <li>Attempt to reverse-engineer, decompile or disassemble any part of the Service.</li>
        <li>Transmit any viruses, malware or other harmful code through the Service.</li>
        <li>Use automated tools, bots or scrapers to access the Service.</li>
        <li>Interfere with or disrupt the integrity or performance of the Service or its servers.</li>
      </ul>

      <h2>5. Intellectual Property</h2>
      <p>
        All content, trademarks, logos, graphics, software and other materials available through
        the Service are the property of Zoku or its licensors and are protected by applicable
        intellectual property laws. Nothing in these Terms grants you any right to use any
        trademark, logo or trade name of Zoku without our prior written consent.
      </p>

      <h2>6. User-Generated Content</h2>
      <p>
        If you submit, upload or share content through the Service (including food photos or
        product images), you grant Zoku a non-exclusive, worldwide, royalty-free licence to use,
        reproduce, process and display that content solely for the purpose of providing and
        improving the Service. You represent that you have all necessary rights to grant this
        licence.
      </p>

      <h2>7. Third-Party Services</h2>
      <p>
        The Service may integrate with or link to third-party services, websites or applications.
        We are not responsible for the content, privacy practices or terms of any third-party
        service. Your use of third-party services is at your own risk and subject to their
        respective terms.
      </p>

      <h2>8. Disclaimer of Warranties</h2>
      <p>
        The Service is provided on an "as is" and "as available" basis without warranties of any
        kind, express or implied, including but not limited to warranties of merchantability,
        fitness for a particular purpose, accuracy, or non-infringement. We do not warrant that
        the Service will be uninterrupted, error-free, or free of viruses or other harmful
        components. Nutritional and ingredient information is sourced from public databases and AI
        analysis and may not always be accurate or complete — always check product labels and
        consult a qualified professional before making health decisions.
      </p>

      <h2>9. Limitation of Liability</h2>
      <p>
        To the fullest extent permitted by applicable law, Zoku shall not be liable for any
        indirect, incidental, special, consequential or punitive damages, including loss of data,
        loss of revenue, or personal injury, arising out of or in connection with your use of the
        Service, even if we have been advised of the possibility of such damages. Our total
        liability to you for any claim shall not exceed the amount you paid us in the twelve
        months preceding the claim, or £50 (or equivalent), whichever is greater.
      </p>

      <h2>10. Indemnification</h2>
      <p>
        You agree to indemnify, defend and hold harmless Zoku and its officers, directors,
        employees and agents from and against any claims, liabilities, damages, losses and
        expenses (including reasonable legal fees) arising out of or in connection with your use
        of the Service, your violation of these Terms, or your violation of any rights of another
        party.
      </p>

      <h2>11. Termination</h2>
      <p>
        We reserve the right to suspend or terminate your access to the Service at any time, with
        or without notice, for any reason, including if we reasonably believe you have violated
        these Terms. Upon termination, all licences granted to you under these Terms will
        immediately cease.
      </p>

      <h2>12. Changes to Terms</h2>
      <p>
        We may update these Terms from time to time. When we do, we will revise the "Last
        updated" date at the top of this page and, where the changes are material, notify you via
        the app or by email. Your continued use of the Service after any such changes constitutes
        your acceptance of the revised Terms.
      </p>

      <h2>13. End User Licence Agreement (Apple)</h2>
      <p>
        If you download or use the Zoku app via Apple's App Store, the following also applies: the
        Zoku app is licensed to you in accordance with Apple's standard End User Licence Agreement
        (EULA). In the event of any conflict between these Terms and the Apple EULA, the Apple
        EULA shall take precedence solely with respect to App Store usage. You can review Apple's
        standard EULA at{" "}
        <a
          href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
          target="_blank"
          rel="noreferrer"
        >
          apple.com/legal/internet-services/itunes/dev/stdeula
        </a>
        .
      </p>
      <p>
        Apple is not responsible for the Zoku app or its content, and has no obligation to provide
        maintenance or support for the app. Apple is not a party to these Terms.
      </p>

      <h2>14. Governing Law</h2>
      <p>
        These Terms shall be governed by and construed in accordance with the laws of England and
        Wales. Any disputes arising out of or relating to these Terms or the Service shall be
        subject to the exclusive jurisdiction of the courts of England and Wales, unless you are a
        consumer resident in another jurisdiction in which case your local consumer protection
        laws may also apply.
      </p>

      <h2>15. Contact</h2>
      <p>
        If you have any questions about these Terms, please contact us at{" "}
        <a href="mailto:support@zoku.in">support@zoku.in</a>.
      </p>
    </LegalShell>
  );
}
